import {
  parseLoomAuthoringCommand,
  parseLoomAuthoringDocument,
  type LoomAirfoil,
  type LoomAuthoringCommand,
  type LoomAuthoringDocument,
  type LoomAxis,
  type LoomSceneNode,
  type LoomWingPanel,
} from "./loomAuthoringModel";
import { loomAuthoringDocumentStateId } from "./loomAuthoringHistory";
import { deriveLoomMirrorAttributes } from "./loomMirrorRelations";
import {
  canonicalLoomSectionJson as canonical,
  DEFAULT_LOOM_SECTION_CONTROL_LIMITS,
  immutableLoomSectionValue as immutable,
  loomHighLiftPayloadDigest,
  loomPatternSectionId,
  parseLoomDroopNoseMorph,
  parseLoomHighLiftDevices,
  parseLoomSectionArtifactReference,
  parseLoomSectionControlLimits as parseLimits,
  parseLoomSectionSourceIdentity as parseSource,
  validateLoomAirfoilAgainstLimits as validateAirfoilLimits,
  validateLoomTreatmentAgainstLimits,
  type LoomDroopNoseMorphDto,
  type LoomHighLiftDeviceDto,
  type LoomSectionArtifactReference,
  type LoomSectionControlLimits,
  type LoomSectionSourceIdentity,
  type LoomWingSectionSide,
} from "./loomSectionRecipe";

export { DEFAULT_LOOM_SECTION_CONTROL_LIMITS, loomHighLiftPayloadDigest, loomPatternSectionId } from "./loomSectionRecipe";
export type { LoomDroopNoseMorphDto, LoomHighLiftDeviceDto, LoomSectionArtifactReference, LoomSectionControlLimits, LoomWingSectionSide } from "./loomSectionRecipe";

export type LoomPatternRole = "recipe-node" | "derived-entity";

export interface LoomPatternSectionProjection {
  kind: "section";
  role: "recipe-node";
  id: string;
  nodeId: string;
  panelId: string;
  side: LoomWingSectionSide;
  family: LoomAirfoil["family"];
  limitsSource: "loom-default-policy" | "section-recipe";
  treatment?: {
    artifactId: string;
    producerVersion: string;
    deviceKinds: readonly LoomHighLiftDeviceDto["kind"][];
    leadingEdgeMorph: "droop-nose" | null;
  };
}
export interface LoomPatternPanelProjection {
  kind: "panel";
  role: "recipe-node";
  id: string;
  panelId: string;
  sections: readonly LoomPatternSectionProjection[];
}
export interface LoomPatternMirrorProjection {
  kind: "mirror-instance";
  role: "derived-entity";
  id: string;
  partnerNodeId: string;
  sourceNodeId: string;
  axis: LoomAxis;
  planeOffsetM: number;
}
export interface LoomPatternWingProjection {
  kind: "wing";
  role: "recipe-node";
  id: string;
  nodeId: string;
  panels: readonly LoomPatternPanelProjection[];
  mirrorInstances: readonly LoomPatternMirrorProjection[];
}
export interface LoomPatternGraphProjection {
  kind: "graph-node";
  role: LoomPatternRole;
  id: string;
  nodeId: string;
  label: string;
  nodeType: LoomSceneNode["type"];
  inputs: readonly LoomPatternGraphProjection[];
  wing?: LoomPatternWingProjection;
  mirrorRelation?: LoomPatternMirrorProjection;
}
export interface LoomPatternOutputProjection {
  kind: "output";
  role: "recipe-node";
  id: string;
  rootNodeId: string;
  graph: LoomPatternGraphProjection;
}
export interface LoomPatternHierarchy {
  schema: "loom-pattern-hierarchy/v1";
  kind: "aircraft-pattern";
  role: "recipe-node";
  id: string;
  documentId: string;
  stateId: string;
  revision: number;
  outputs: readonly LoomPatternOutputProjection[];
}

export type LoomSectionSource = LoomSectionSourceIdentity;
export interface LoomHighLiftSectionRequest {
  schema: "loom-high-lift-section-request/v1";
  requestId: string;
  source: LoomSectionSource;
  sectionDefinition: LoomAirfoil;
  limits: LoomSectionControlLimits;
}
export interface LoomHighLiftSectionReturn {
  schema: "loom-high-lift-section-return/v1";
  requestId: string;
  source: LoomSectionSource;
  artifact: LoomSectionArtifactReference;
  sectionDefinition: LoomAirfoil;
  devices: readonly LoomHighLiftDeviceDto[];
  leadingEdgeMorph?: LoomDroopNoseMorphDto;
}
export interface LoomCompiledHighLiftReturn {
  command: Extract<LoomAuthoringCommand, { kind: "primitive.replace" }>;
}

type RecordValue = Record<string, unknown>;
const object = (value: unknown, path: string): RecordValue => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Error(`${path} must be an object`);
  return value as RecordValue;
};
const exact = (value: RecordValue, keys: readonly string[], path: string): void => {
  const unknown = Object.keys(value).filter((key) => !keys.includes(key));
  if (unknown.length) throw new Error(`${path} contains unknown fields: ${unknown.join(", ")}`);
};
const text = (value: unknown, path: string): string => {
  if (typeof value !== "string" || value.trim() === "") throw new Error(`${path} must be a non-empty string`);
  return value;
};
const stableId = (value: unknown, path: string): string => {
  const result = text(value, path);
  if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{0,511}$/.test(result)) throw new Error(`${path} is not a stable id`);
  return result;
};
const finite = (value: unknown, path: string): number => {
  if (typeof value !== "number" || !Number.isFinite(value)) throw new Error(`${path} must be finite`);
  return value;
};
const choice = <T extends string>(value: unknown, choices: readonly T[], path: string): T => {
  if (typeof value !== "string" || !choices.includes(value as T)) throw new Error(`${path} is unsupported`);
  return value as T;
};
function parseAirfoil(value: unknown, path: string): LoomAirfoil {
  const item = object(value, path);
  const family = choice(item.family, ["naca4", "cst", "polygon"] as const, `${path}.family`);
  if (family === "naca4") {
    exact(item, ["family", "camberRatio", "camberLocationRatio", "thicknessRatio", "closedTrailingEdge", "trailingEdgeThicknessRatio"], path);
    if (typeof item.closedTrailingEdge !== "boolean") throw new Error(`${path}.closedTrailingEdge must be boolean`);
    return { family, camberRatio: finite(item.camberRatio, `${path}.camberRatio`), camberLocationRatio: finite(item.camberLocationRatio, `${path}.camberLocationRatio`), thicknessRatio: finite(item.thicknessRatio, `${path}.thicknessRatio`), closedTrailingEdge: item.closedTrailingEdge, trailingEdgeThicknessRatio: finite(item.trailingEdgeThicknessRatio, `${path}.trailingEdgeThicknessRatio`) };
  }
  if (family === "cst") {
    exact(item, ["family", "upperCoefficients", "lowerCoefficients"], path);
    if (!Array.isArray(item.upperCoefficients) || !Array.isArray(item.lowerCoefficients) || item.upperCoefficients.length === 0 || item.upperCoefficients.length !== item.lowerCoefficients.length) throw new Error(`${path} CST arrays must be non-empty and equal-length`);
    return { family, upperCoefficients: item.upperCoefficients.map((entry, index) => finite(entry, `${path}.upperCoefficients[${index}]`)), lowerCoefficients: item.lowerCoefficients.map((entry, index) => finite(entry, `${path}.lowerCoefficients[${index}]`)) };
  }
  exact(item, ["family", "vertices", "closedTrailingEdge", "trailingEdgeThicknessRatio"], path);
  if (!Array.isArray(item.vertices) || item.vertices.length < 3 || typeof item.closedTrailingEdge !== "boolean") throw new Error(`${path} polygon is malformed`);
  const vertices = item.vertices.map((point, index) => {
    if (!Array.isArray(point) || point.length !== 2) throw new Error(`${path}.vertices[${index}] must have two entries`);
    return [finite(point[0], `${path}.vertices[${index}][0]`), finite(point[1], `${path}.vertices[${index}][1]`)] as const;
  });
  return { family, vertices, closedTrailingEdge: item.closedTrailingEdge, trailingEdgeThicknessRatio: finite(item.trailingEdgeThicknessRatio, `${path}.trailingEdgeThicknessRatio`) };
}
function locateSection(document: LoomAuthoringDocument, source: LoomSectionSource): { node: Extract<LoomSceneNode, { type: "primitive" }>; panel: LoomWingPanel; section: LoomAirfoil } {
  const node = document.nodes.find((candidate): candidate is Extract<LoomSceneNode, { type: "primitive" }> => candidate.id === source.nodeId && candidate.type === "primitive");
  if (!node || node.definition.primitive !== "wing") throw new Error(`source node ${source.nodeId} is not a wing`);
  const panel = node.definition.panels.find((candidate) => candidate.panelId === source.panelId);
  if (!panel) throw new Error(`source panel ${source.panelId} does not exist on ${source.nodeId}`);
  return { node, panel, section: source.side === "root" ? panel.airfoilRoot : panel.airfoilTip };
}

export function createLoomPatternHierarchy(documentValue: unknown): LoomPatternHierarchy {
  const document = parseLoomAuthoringDocument(documentValue); const stateId = loomAuthoringDocumentStateId(document); const byId = new Map(document.nodes.map((node) => [node.id, node])); const mirrors = deriveLoomMirrorAttributes(document);
  const mirrorProjection = (sourceNodeId: string, partnerNodeId: string, axis: LoomAxis, planeOffsetM: number): LoomPatternMirrorProjection => ({ kind: "mirror-instance", role: "derived-entity", id: `loom-mirror:${stateId}:${sourceNodeId}:${partnerNodeId}`, sourceNodeId, partnerNodeId, axis, planeOffsetM });
  const project = (nodeId: string): LoomPatternGraphProjection => {
    const node = byId.get(nodeId); if (!node) throw new Error(`graph node ${nodeId} is missing`);
    const inputs = node.type === "transform" ? [project(node.inputNodeId)] : node.type === "boolean" ? node.inputNodeIds.map(project) : [];
    const mirrorRelation = node.type === "transform" && node.operation.transform === "mirror" ? mirrorProjection(node.inputNodeId, node.id, node.operation.axis, node.operation.planeOffsetM) : undefined;
    let wing: LoomPatternWingProjection | undefined;
    if (node.type === "primitive" && node.definition.primitive === "wing") {
      wing = { kind: "wing", role: "recipe-node", id: `loom-wing:${stateId}:${node.id}`, nodeId: node.id, panels: node.definition.panels.map((panel) => ({ kind: "panel", role: "recipe-node", id: `loom-panel:${stateId}:${node.id}:${panel.panelId}`, panelId: panel.panelId, sections: (["root", "tip"] as const).map((side) => { const recipe = panel.sectionRecipes?.[side]; const treatment = recipe?.treatment; return { kind: "section", role: "recipe-node", id: loomPatternSectionId({ documentId: document.documentId, stateId, revision: document.revision, nodeId: node.id, panelId: panel.panelId, side }), nodeId: node.id, panelId: panel.panelId, side, family: side === "root" ? panel.airfoilRoot.family : panel.airfoilTip.family, limitsSource: recipe ? "section-recipe" : "loom-default-policy", ...(treatment ? { treatment: { artifactId: treatment.artifact.artifactId, producerVersion: treatment.artifact.producerVersion, deviceKinds: treatment.devices.map(({ kind }) => kind), leadingEdgeMorph: treatment.leadingEdgeMorph?.kind ?? null } } : {}) }; } ) })), mirrorInstances: mirrors.filter((relation) => relation.sourceNodeId === node.id).map((relation) => mirrorProjection(relation.sourceNodeId, relation.partnerNodeId, relation.axis, relation.planeOffsetM)) };
    }
    return immutable({ kind: "graph-node", role: mirrorRelation ? "derived-entity" : "recipe-node", id: `loom-graph:${stateId}:${node.id}`, nodeId: node.id, label: node.label, nodeType: node.type, inputs, ...(wing ? { wing } : {}), ...(mirrorRelation ? { mirrorRelation } : {}) });
  };
  return immutable({ schema: "loom-pattern-hierarchy/v1", kind: "aircraft-pattern", role: "recipe-node", id: `loom-pattern:${document.documentId}:${stateId}`, documentId: document.documentId, stateId, revision: document.revision, outputs: document.rootNodeIds.map((rootNodeId) => ({ kind: "output", role: "recipe-node", id: `loom-output:${stateId}:${rootNodeId}`, rootNodeId, graph: project(rootNodeId) })) });
}

export function parseLoomHighLiftSectionRequest(value: unknown): LoomHighLiftSectionRequest {
  const item = object(value, "request"); exact(item, ["schema", "requestId", "source", "sectionDefinition", "limits"], "request");
  if (item.schema !== "loom-high-lift-section-request/v1") throw new Error("request.schema must be loom-high-lift-section-request/v1");
  const result = { schema: "loom-high-lift-section-request/v1" as const, requestId: stableId(item.requestId, "request.requestId"), source: parseSource(item.source, "request.source"), sectionDefinition: parseAirfoil(item.sectionDefinition, "request.sectionDefinition"), limits: parseLimits(item.limits) };
  validateAirfoilLimits(result.sectionDefinition, result.limits); return immutable(result);
}
export function createLoomHighLiftSectionRequest(options: { document: unknown; nodeId: string; panelId: string; side: LoomWingSectionSide; requestId: string }): LoomHighLiftSectionRequest {
  const document = parseLoomAuthoringDocument(options.document); const stateId = loomAuthoringDocumentStateId(document); const partial = { documentId: document.documentId, stateId, revision: document.revision, nodeId: stableId(options.nodeId, "nodeId"), panelId: stableId(options.panelId, "panelId"), side: options.side }; const source = { ...partial, sectionId: loomPatternSectionId(partial) }; const located = locateSection(document, source);
  const storedLimits = located.panel.sectionRecipes?.[options.side]?.limits;
  return parseLoomHighLiftSectionRequest({ schema: "loom-high-lift-section-request/v1", requestId: options.requestId, source, sectionDefinition: located.section, limits: storedLimits ?? DEFAULT_LOOM_SECTION_CONTROL_LIMITS });
}
export function parseLoomHighLiftSectionReturn(value: unknown): LoomHighLiftSectionReturn {
  const item = object(value, "return"); exact(item, ["schema", "requestId", "source", "artifact", "sectionDefinition", "devices", "leadingEdgeMorph"], "return");
  if (item.schema !== "loom-high-lift-section-return/v1") throw new Error("return.schema must be loom-high-lift-section-return/v1");
  const source = parseSource(item.source, "return.source"); const sectionDefinition = parseAirfoil(item.sectionDefinition, "return.sectionDefinition"); const devices = parseLoomHighLiftDevices(item.devices, "return.devices"); const leadingEdgeMorph = item.leadingEdgeMorph === undefined ? undefined : parseLoomDroopNoseMorph(item.leadingEdgeMorph, "return.leadingEdgeMorph"); const artifact = parseLoomSectionArtifactReference(item.artifact, "return.artifact");
  if (artifact.payloadDigest !== loomHighLiftPayloadDigest(source, sectionDefinition, devices, leadingEdgeMorph)) throw new Error("return.artifact.payloadDigest does not match the returned treatment");
  return immutable({ schema: "loom-high-lift-section-return/v1", requestId: stableId(item.requestId, "return.requestId"), source, artifact, sectionDefinition, devices, ...(leadingEdgeMorph ? { leadingEdgeMorph } : {}) });
}
export function compileLoomHighLiftSectionReturn(options: { document: unknown; request: unknown; returned: unknown; commandId: string }): LoomCompiledHighLiftReturn {
  const document = parseLoomAuthoringDocument(options.document); const request = parseLoomHighLiftSectionRequest(options.request); const returned = parseLoomHighLiftSectionReturn(options.returned);
  if (request.requestId !== returned.requestId || canonical(request.source) !== canonical(returned.source)) throw new Error("High-Lift return targets the wrong request or section source");
  if (document.documentId !== request.source.documentId || document.revision !== request.source.revision || loomAuthoringDocumentStateId(document) !== request.source.stateId) throw new Error("High-Lift return is stale for the current Pattern head");
  const located = locateSection(document, request.source); if (canonical(located.section) !== canonical(request.sectionDefinition)) throw new Error("High-Lift request section no longer matches its source"); validateAirfoilLimits(returned.sectionDefinition, request.limits); validateLoomTreatmentAgainstLimits(returned.devices, returned.leadingEdgeMorph, request.limits);
  const definition = located.node.definition; if (definition.primitive !== "wing") throw new Error("section source is not a wing");
  const recipe = { schema: "loom-section-recipe/v1" as const, limits: request.limits, treatment: { schema: "loom-high-lift-treatment/v1" as const, source: returned.source, artifact: returned.artifact, devices: returned.devices, ...(returned.leadingEdgeMorph ? { leadingEdgeMorph: returned.leadingEdgeMorph } : {}) } };
  const panels = definition.panels.map((panel) => { if (panel.panelId !== request.source.panelId) return panel; const sectionRecipes = { ...panel.sectionRecipes, [request.source.side]: recipe }; return request.source.side === "root" ? { ...panel, airfoilRoot: returned.sectionDefinition, sectionRecipes } : { ...panel, airfoilTip: returned.sectionDefinition, sectionRecipes }; });
  const command = parseLoomAuthoringCommand({ schema: "loom-authoring-command/v1", commandId: options.commandId, expectedRevision: request.source.revision, kind: "primitive.replace", nodeId: request.source.nodeId, definition: { ...definition, panels } });
  if (command.kind !== "primitive.replace") throw new Error("compiled command must be primitive.replace");
  return immutable({ command });
}
